import { PropertyType } from '@/backend/types'
import { LDEvents, useABMetrics } from '@/modules/App/useABMetrics'
import { useProperty } from '@/modules/Project/useProperty.ts'
import { useWorkspaces } from '@/modules/Workspaces/useWorkspaces'
import { toast } from '@/shared/toast'
import { isCommandOrControlKey } from '@/shared/utils/event'
import { assertIsNotNullOrUndefined } from '@/shared/utils/typeAssertions'
import { isCollectionProperty, isSelectProperty } from '@/shared/utils/typeGuards'
import { usePopoverStore } from '@/sharedComposables/popoverStore'
import { captureException } from '@sentry/vue'
import { createSharedComposable } from '@vueuse/core'
import { computed, ref, useId, type ComponentPublicInstance } from 'vue'
import { useRelatedProjectsStore } from './relatedProjectsStore'
import { useProjectActions } from './useProjectActions'
import { useProjectStore } from './useProjectStore'
import { usePropertyDialogIntegration } from './usePropertyDialogIntegration'

/**
 * This composable provides logic, state and event handlers for the popover menu
 * used to configure properties.
 */
export const usePropertyDialog = createSharedComposable(() => {
  const relatedProjectsStore = useRelatedProjectsStore()
  const workspacesStore = useWorkspaces()
  const propertyStore = useProperty()
  const projectStore = useProjectStore()

  const {
    deleteProperty,
    deletePropertyConfirmationOpen,
    deselectProperty,
    hideProperty,
    reprocessColumn,
    saveProperty,
    createProperty,
  } = usePropertyDialogIntegration()

  const closeMenu = () => deselectProperty()

  const isExpanded = ref(false)
  const anchorRef = ref<HTMLElement | null>(null)
  const anchorRefsById = ref<Record<string, HTMLElement | ComponentPublicInstance | null>>({})

  const selectForEditing = async (propertyId: string, ref: HTMLElement | null) => {
    if (propertyStore.editedProperty) {
      const savedOk = await updateProperty()
      if (!savedOk) return
    }

    if (propertyStore.editedProperty?.id !== propertyId) {
      projectStore.setSelectedProperty(propertyId)
      anchorRef.value = ref
    } else {
      deselectProperty()
      anchorRef.value = null
    }
  }

  /**
   * For auto-property, because it was migrated in from another module,
   * we use useProjectActions, which has optimistic UI support.
   */
  const projectActions = useProjectActions()
  const createAutoProperty = async () => {
    const property = propertyStore.editedProperty
    if (!property) {
      return
    }

    try {
      /**
       * When creating an auto-property, the config contains the
       * options suggested by the backend, and the upserted options
       * are the ones that the user has reviewed and approved.
       */
      if (isSelectProperty(property)) {
        property.config.options =
          propertyStore.upsertOptions?.map((o) => ({
            value: o.new_value ?? o.value,
            color: o.color,
            tool_fallback: o.tool_fallback || false,
          })) || []
      } else if (isCollectionProperty(property)) {
        property.config.properties =
          propertyStore.upsertCollectionProperties?.map((p) => ({
            name: p.new_name ?? p.name,
            type: p.new_type ?? p.type ?? 'text',
          })) || []
      }

      projectStore.setSelectedProperty(null)
      await projectActions.createProperty(property)
      return true
    } catch {
      return false
    }
  }

  /**
   * We want to avoid creation of duplicate props,
   * so we hold a reference to the create request, to wait for it once it exists.
   */
  let createRequest: ReturnType<typeof createProperty> | null = null

  /**
   * Called when saving changes to a property.
   */
  const updateProperty = async () => {
    const updatedProperty = propertyStore.editedProperty
    if (!updatedProperty) {
      return
    }

    if (!createRequest && updatedProperty.type === 'reference' && updatedProperty.isOptimistic) {
      createRequest = createProperty(updatedProperty)
      const res = await createRequest

      if (!res.ok) {
        captureException(new Error('Failed to create property'), { data: res.error })
        toast.error('Failed to create property')
      } else {
        assertIsNotNullOrUndefined(workspacesStore.currentWorkspace)
        await relatedProjectsStore.loadRelatedProject({
          property: { ...updatedProperty, id: res.data.id },
          workspaceId: workspacesStore.currentWorkspace.id,
        })
      }

      return res.ok
    }
    if (createRequest) {
      await createRequest
      createRequest = null
    }

    return saveProperty()
  }

  const { trackElapsedTimeEvent } = useABMetrics()

  const updatePropertyAndCloseMenu = async () => {
    const savedOk = await updateProperty()
    if (!savedOk) return

    trackElapsedTimeEvent(LDEvents.TIME_SPENT_ON_EDITING)
    isExpanded.value = false
    deselectProperty()
  }

  const onReprocessColumn = async () => {
    if (!(await saveProperty())) return // leave the menu open if the save failed
    await reprocessColumn()
    deselectProperty()
  }

  const openDeleteDialog = () => {
    deletePropertyConfirmationOpen.value = true
  }

  const canCloseCollectionMenu = computed<boolean>(() => {
    return (
      PropertyType.collection !== propertyStore.editedProperty?.type ||
      (PropertyType.collection === propertyStore.editedProperty?.type &&
        propertyStore.editedProperty?.tool === 'manual') ||
      propertyStore.visibleProperties.length > 0
    )
  })

  const collectionError = computed(() => !canCloseCollectionMenu.value)

  /** The user must have selected a related agent in order to save a reference property. */
  const canSaveReferenceProperty = computed(() => {
    if (propertyStore.editedProperty?.type !== 'reference') {
      return true
    }

    return !!propertyStore.editedProperty.config.projectId
  })

  const popoverStore = usePopoverStore()
  const relatedProjectMenuItemId = useId()
  const closeRelatedProjectValidationPopover = () => {
    if (popoverStore.popover?.targetSelector === `#${relatedProjectMenuItemId}`) {
      popoverStore.setPopover(null)
    }
  }

  const validateCanSave = (): boolean => {
    if (!canCloseCollectionMenu.value) {
      return false
    }

    if (!canSaveReferenceProperty.value) {
      popoverStore.setPopover({
        text: 'Please select a source',
        placement: { allowedPlacements: ['left', 'right'] },
        targetSelector: `#${relatedProjectMenuItemId}`,
      })
      return false
    }

    return true
  }

  const onPromptKeydown = async (e: KeyboardEvent) => {
    if (!(e.key === 'Enter' && isCommandOrControlKey(e))) return

    e.stopPropagation()

    const canSave = validateCanSave()
    if (!canSave) {
      return
    }

    const savedOk = await updateProperty()
    if (!savedOk) return

    deselectProperty()
  }

  const handleClickOutside = async (e: Event | PointerEvent) => {
    if (isExpanded.value) {
      return
    }

    const target = e.target as HTMLElement

    if (
      target instanceof HTMLElement &&
      Object.values(anchorRefsById.value).some((ref) => {
        if (ref instanceof HTMLElement) {
          return ref === target || ref.contains(target)
        }
        if (ref) {
          return ref.$el === target || ref.$el.contains(target)
        }
        return false
      })
    ) {
      return
    }

    if (!validateCanSave()) return

    const propertyIdToUpdate = propertyStore.editedProperty?.id
    // Save the unsaved changes and close the dialog
    if (propertyStore.savedProperty && propertyStore.isDirty) {
      const savedOk = await updateProperty()
      if (!savedOk) return
    }

    /**
     * This `if` condition was added to prevent flakey tests, but it could also
     * be a real problem in the app on particularly slow connections.
     *
     * The problem happens when:
     * 1. User clicks outside, we make API request to save changes
     * 2. Some other thing happens in the app to deselect the property
     * 3. User selects another property
     * 4. API request from (1) resolves and we deselect the property that
     *    the user just selected in (3).
     */
    if (propertyIdToUpdate === propertyStore.editedProperty?.id) {
      deselectProperty()
    }
  }

  return {
    anchorRef,
    anchorRefsById,
    canCloseCollectionMenu,
    canSaveReferenceProperty,
    closeMenu,
    closeRelatedProjectValidationPopover,
    collectionError,
    createAutoProperty,
    deleteProperty,
    deletePropertyConfirmationOpen,
    handleClickOutside,
    hideProperty,
    isExpanded,
    onPromptKeydown,
    onReprocessColumn,
    openDeleteDialog,
    relatedProjectMenuItemId,
    selectForEditing,
    updateProperty,
    updatePropertyAndCloseMenu,
    validateCanSave,
  }
})
